import { get } from "lodash";
import moment from "moment";
import { GENDER, IBAN_LENGTHS } from "utils/constants";

export const costToFrench = (value, precision = 2) => {
  const number = Number(value);
  if (isNaN(number)) return value;

  return number.toLocaleString("fr-FR", {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  });
};

export const getPhonePrefix = (phoneNumber) => {
  const phone = phoneNumber.replace(/\s+/g, " ").slice(0, -9);
  const match = phone.match(/^(?:\+|00)(\d{1,3})/);
  return match ? `+${match[1]}` : "+33";
};

const mod97 = (str) => {
  let remainder = 0;
  for (let i = 0; i < str.length; i++)
    remainder = (remainder * 10 + Number(str[i])) % 97;
  return remainder;
};

const isLetters2 = (s) => /^[A-Z]{2}$/.test(s);
const isAlnum = (s) => /^[A-Z0-9]+$/.test(s);
const normalizeIban = (input) => {
  if (input == null) return "";
  return String(input)
    .trim()
    .toUpperCase()
    .replace(/[\s\u00A0-]+/g, "");
};

const alnumToDigits = (str) => {
  let out = "";
  for (let i = 0; i < str.length; i++) {
    const c = str[i];
    const code = str.charCodeAt(i);
    if (code >= 48 && code <= 57) {
      out += c;
    } else {
      const v = code - 55;
      out += String(v);
    }
  }
  return out;
};

export const validateNIR = (raw, parent = {}) => {
  if (!raw) return { valid: false, reason: "REQUIRED" };

  const s = raw.replace(/[\s.\-_/]/g, "").toUpperCase();
  if (s.length !== 15)
    return { valid: false, reason: "INVALID_SOCIAL_SECURITY_LENGTH" };

  const gender = get(parent, "user.gender");
  const birthDate = get(parent, "user.birthDate");

  if (gender && birthDate) {
    const prefix = `${
      (gender === GENDER.MALE && "[17]") ||
      (gender === GENDER.FEMALE && "[28]") ||
      "[1278]"
    }${moment(birthDate).format("YY")}${moment(birthDate).format("MM")}`;

    const regexPattern = new RegExp(`^${prefix}(?:[0-9]{8}|[0-9]{10})$`);
    if (!regexPattern.test(s)) {
      return { valid: false, reason: "INVALID_SOCIAL_SECURITY_NUMBER" };
    }
  }
  const keyStr = s.slice(13, 15);
  if (!/^\d{2}$/.test(keyStr))
    return { valid: false, reason: "INVALID_SOCIAL_SECURITY_KEY" };

  const dep = s.slice(5, 7);
  let core = s.slice(0, 13);
  if (dep === "2A" || dep === "2B") {
    const depMapped = dep === "2A" ? "19" : "18";
    core = core.slice(0, 5) + depMapped + core.slice(7);
  } else if (!/^\d{2}$/.test(dep)) {
    return {
      valid: false,
      reason: "INVALID_DEPARTMENT_CODE",
    };
  }

  if (!/^\d{13}$/.test(core))
    return { valid: false, reason: "INVALID_SOCIAL_SECURITY_NUMBER" };
  const mod = mod97(core);
  const calcKey = 97 - mod;
  const calcKeyStr = calcKey.toString().padStart(2, "0");
  if (calcKeyStr !== keyStr)
    return { valid: false, reason: "INVALID_SOCIAL_SECURITY_KEY" };

  return { valid: true };
};

export const softValidateNIR = (raw) => {
  if (!raw) return { valid: false, reason: "REQUIRED" };

  const s = raw.replace(/[\s.\-_/]/g, "").toUpperCase();
  if (s.length !== 15)
    return { valid: false, reason: "INVALID_SOCIAL_SECURITY_LENGTH" };

  if (!/^[1278]/.test(s))
    return { valid: false, reason: "INVALID_SOCIAL_SECURITY_NUMBER" };

  const yearPart = s.slice(1, 3);
  if (!/^\d{2}$/.test(yearPart))
    return { valid: false, reason: "INVALID_SOCIAL_SECURITY_NUMBER" };

  const monthPart = s.slice(3, 5);
  const monthNum = parseInt(monthPart, 10);
  if (!/^\d{2}$/.test(monthPart) || monthNum < 1 || monthNum > 12)
    return { valid: false, reason: "INVALID_SOCIAL_SECURITY_NUMBER" };

  const keyStr = s.slice(13, 15);
  if (!/^\d{2}$/.test(keyStr))
    return { valid: false, reason: "INVALID_SOCIAL_SECURITY_KEY" };

  const dep = s.slice(5, 7);
  let core = s.slice(0, 13);
  if (dep === "2A" || dep === "2B") {
    const depMapped = dep === "2A" ? "19" : "18";
    core = core.slice(0, 5) + depMapped + core.slice(7);
  } else if (!/^\d{2}$/.test(dep)) {
    return {
      valid: false,
      reason: "INVALID_DEPARTMENT_CODE",
    };
  }

  if (!/^\d{13}$/.test(core))
    return { valid: false, reason: "INVALID_SOCIAL_SECURITY_NUMBER" };
  const mod = mod97(core);
  const calcKey = 97 - mod;
  const calcKeyStr = calcKey.toString().padStart(2, "0");
  if (calcKeyStr !== keyStr)
    return { valid: false, reason: "INVALID_SOCIAL_SECURITY_KEY" };

  return { valid: true };
};

export const validateIBAN = (input) => {
  const iban = normalizeIban(input);
  if (iban.length < 4) return { valid: false, reason: "IBAN.TOO_SHORT" };

  const country = iban.slice(0, 2);
  if (!isLetters2(country))
    return {
      valid: false,
      reason: "IBAN.BAD_COUNTRY_CODE",
    };

  if (!isAlnum(iban))
    return {
      valid: false,
      reason: "IBAN.BAD_CHARSET",
    };

  const expected = IBAN_LENGTHS[country];
  if (!expected)
    return {
      valid: false,
      reason: "IBAN.UNSUPPORTED_COUNTRY",
    };

  if (iban.length !== expected)
    return {
      valid: false,
      reason: "IBAN.BAD_LENGTH",
    };

  const rearranged = iban.slice(4) + iban.slice(0, 4);
  const digitStr = alnumToDigits(rearranged);
  const remainder = mod97(digitStr);

  if (remainder === 1) return { valid: true };

  return {
    valid: false,
    reason: "IBAN.MODE97_FAILED",
  };
};

const normalizeBic = (input) => {
  if (input == null) return "";
  return String(input)
    .trim()
    .toUpperCase()
    .replace(/[\s\u00A0-]+/g, "");
};

export const validateBIC = (input) => {
  const bic = normalizeBic(input);

  if (!bic) return { valid: false, reason: "BIC.REQUIRED" };

  // BIC format: 4 letters (bank code) + 2 letters (country code) + 2 alphanumeric (location) + 3 alphanumeric optional (branch)
  // Total length: 8 or 11 characters
  if (bic.length !== 8 && bic.length !== 11) {
    return {
      valid: false,
      reason: "BIC.BAD_LENGTH",
    };
  }

  // All characters must be alphanumeric
  if (!isAlnum(bic)) {
    return {
      valid: false,
      reason: "BIC.BAD_CHARSET",
    };
  }

  // First 4 characters must be letters (bank code)
  if (!/^[A-Z]{4}/.test(bic)) {
    return {
      valid: false,
      reason: "BIC.BAD_BANK_CODE",
    };
  }

  // Characters 5-6 must be letters (country code)
  const countryCode = bic.slice(4, 6);
  if (!isLetters2(countryCode)) {
    return {
      valid: false,
      reason: "BIC.BAD_COUNTRY_CODE",
    };
  }

  // Characters 7-8 must be alphanumeric (location)
  const location = bic.slice(6, 8);
  if (!/^[A-Z0-9]{2}$/.test(location)) {
    return {
      valid: false,
      reason: "BIC.BAD_LOCATION",
    };
  }

  // Characters 9-11 (if present) must be alphanumeric (branch)
  if (bic.length === 11) {
    const branch = bic.slice(8, 11);
    if (!/^[A-Z0-9]{3}$/.test(branch)) {
      return {
        valid: false,
        reason: "BIC.BAD_BRANCH",
      };
    }
  }

  return { valid: true };
};
